import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Search, X } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import type {
  BloqueTexto,
  CanalResena,
  DetalleListing,
  RangoResenas,
  ResenaItem,
  SubnotaAgregada,
} from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import AuditoriaListing from '@/components/reviews/AuditoriaListing';
import { classNames } from '@/lib/utils';
import { monthLabel, useChartTheme } from '@/components/aperturas/shared';
import {
  BadgesListing,
  BarraSubnota,
  Chip,
  Estrellas,
  ETIQUETA_CANAL,
  ETIQUETA_ESTADO,
  EtiquetaCanal,
  NotaGrande,
  SelectorRango,
  fechaCorta,
  fmt,
  hace,
  useRangoResenas,
  PieInfinito,
  useScrollInfinito,
} from '@/components/reviews/shared';

/**
 * Ficha de un alojamiento con todas sus reseñas.
 *
 * Vive fuera del layout de pestañas de Reseñas a propósito: dentro gastaría 96 px
 * de cabecera más 44 px de pestañas antes de la primera reseña, y en 375 px eso
 * es un cuarto de pantalla de cromo que no sirve para leer. A cambio trae su
 * propia barra de volver.
 *
 * La jerarquía de la pantalla es una decisión, no un descuido: lo positivo se
 * recorta y lo mejorable no. Sobre `improve` y sobre la nota privada al
 * anfitrión es sobre lo que se actúa, y esconderlas detrás de un segundo toque
 * las saca del trabajo del lunes.
 */

const POR_PAGINA = 25;

type Filtro = 'todas' | 'negativas' | 'texto';

/**
 * Canales del selector, fijos y no deducidos de `porCanal` del listing.
 *
 * Si se construyeran con los canales presentes en la respuesta, filtrar por
 * Booking dejaría un desplegable con una sola opción —la ya elegida— y el
 * usuario no tendría por dónde volver.
 */
const CANALES: CanalResena[] = ['airbnb', 'booking', 'vrbo', 'otras'];

/**
 * El ancho se consulta en JavaScript, no en CSS, porque de esto depende que un
 * componente se **monte** o no. Con `hidden` recharts seguiría construyendo el
 * gráfico en el móvil, y los recuentos seguirían ocupando su ancho dentro de
 * unos chips que en 375 px no caben con ellos.
 */
function useAnchoMinimo(px: number): boolean {
  const [cabe, setCabe] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(min-width: ${px}px)`).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${px}px)`);
    const alCambiar = (e: MediaQueryListEvent) => setCabe(e.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, [px]);
  return cabe;
}

/**
 * El bloque solo si trae texto de verdad.
 *
 * Hostify guarda cadena vacía donde el huésped no escribió nada, y un objeto
 * con `texto: ''` es tan verdadero como uno lleno: sin este filtro la tarjeta
 * pintaría el rótulo «Lo mejorable» y su franja roja encima de la nada.
 */
function conTexto(bloque: BloqueTexto | null): BloqueTexto | null {
  return bloque && bloque.texto.trim() ? bloque : null;
}

/** `2021` · `2021–2026`. El guion largo es el de rango, no el de resta. */
function rangoDeAnios(primera: string | null, ultima: string | null): string {
  const a = primera?.slice(0, 4);
  const b = ultima?.slice(0, 4);
  if (!a && !b) return '';
  if (!a || !b || a === b) return a ?? b ?? '';
  return `${a}–${b}`;
}

// ─── Bloques de texto de una reseña ─────────────────────────────────────────

/**
 * Un bloque de texto de la reseña, con su rótulo y su franja de color.
 *
 * `recortar` solo se pasa en lo positivo. Lo mejorable y la nota privada llegan
 * enteros siempre: son la razón por la que alguien abre esta pantalla.
 *
 * Cuando el texto es una traducción se dice, y se puede ver el original en el
 * sitio. Con casi dos mil reseñas y apenas medio centenar traducidas, callarlo
 * sería hacer pasar por palabras del huésped algo que no lo es.
 */
function BloqueResena({
  bloque,
  rotulo,
  claseRotulo,
  claseCaja,
  claseTexto,
  recortar = false,
}: {
  bloque: BloqueTexto;
  rotulo: string;
  claseRotulo: string;
  claseCaja: string;
  claseTexto: string;
  recortar?: boolean;
}) {
  const [expandido, setExpandido] = useState(false);
  const [verOriginal, setVerOriginal] = useState(false);
  const [recortable, setRecortable] = useState(false);
  const refTexto = useRef<HTMLParagraphElement>(null);

  // Hay reseñas traducidas cuyo original llega en blanco. Ofrecer «ver
  // original» para acabar enseñando una tarjeta vacía es peor que no ofrecerlo.
  const original = bloque.original?.trim() ? bloque.original : null;
  const texto = verOriginal && original ? original : bloque.texto;

  // Se mide en lugar de estimar por longitud: tres líneas son distintas en
  // 375 px y en la columna de 68ch del escritorio, y un botón «Leer más» que no
  // despliega nada gasta 44 px y confianza. Al expandir no se vuelve a medir
  // —el efecto sale antes—, así que el botón sigue ahí para poder plegar.
  useLayoutEffect(() => {
    if (!recortar || expandido) return;
    const el = refTexto.current;
    if (!el) return;
    setRecortable(el.scrollHeight - el.clientHeight > 2);
  }, [recortar, expandido, texto]);

  return (
    <div className={claseCaja}>
      <div className="flex flex-wrap items-center gap-x-2">
        <span className={classNames('text-[10px] font-medium uppercase tracking-wide', claseRotulo)}>
          {rotulo}
        </span>
        {bloque.traducido && original && (
          <>
            <span className="badge bg-slate-100 text-slate-600 text-[10px]">
              {verOriginal ? 'Original' : 'Traducido'}
            </span>
            <button
              type="button"
              onClick={() => setVerOriginal((v) => !v)}
              className="inline-flex items-center min-h-11 px-1 text-[11px] text-slate-500 underline underline-offset-2"
            >
              {verOriginal ? 'ver traducción' : 'ver original'}
            </button>
          </>
        )}
      </div>

      <p
        ref={refTexto}
        className={classNames(
          claseTexto,
          'whitespace-pre-line',
          recortar && !expandido && 'line-clamp-3',
        )}
      >
        {texto}
      </p>

      {recortar && recortable && (
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          className="w-full h-11 mt-1 border-t border-slate-100 text-[13px] text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5"
        >
          {expandido ? 'Leer menos' : 'Leer más'}
        </button>
      )}
    </div>
  );
}

// ─── Tarjeta de reseña ──────────────────────────────────────────────────────

function TarjetaResena({ resena, listingId }: { resena: ResenaItem; listingId: string }) {
  const comentario = conTexto(resena.comentario);
  const love = conTexto(resena.love);
  const improve = conTexto(resena.improve);
  const feedback = conTexto(resena.feedback);

  const flojas = resena.subnotas.filter((s) => s.floja && s.sobre5 !== null);
  const hayTraduccion = [comentario, love, improve, feedback].some((b) => b?.traducido);
  const idioma = resena.idioma?.slice(0, 2).toUpperCase();
  const sinTraducir = !hayTraduccion && !!idioma && resena.idioma !== 'es';

  return (
    <article className="card p-3 space-y-2">
      {/* Meta. La nota nativa de Booking va debajo de las estrellas como texto
          visible y nunca en un `title`: en un móvil no hay hover y el 8,6 que el
          propietario reconoce de su extranet quedaría inalcanzable. */}
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] text-slate-500 min-w-0">
          {[fechaCorta(resena.fecha), resena.canalEtiqueta, resena.huesped]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <div className="flex flex-col items-end shrink-0">
          <Estrellas nota={resena.nota5} />
          {resena.escala === 10 && resena.notaNativa !== null && (
            <span className="text-[11px] text-slate-500 tabular-nums mt-0.5">
              {fmt(resena.notaNativa, 1)}/10
            </span>
          )}
        </div>
      </div>

      {!!resena.titulo?.trim() && <h3 className="text-[15px] font-semibold">{resena.titulo}</h3>}

      {comentario && (
        <BloqueResena
          bloque={comentario}
          rotulo="Comentario público"
          claseRotulo="text-slate-500"
          claseCaja=""
          claseTexto="text-[15px] leading-relaxed"
          recortar
        />
      )}

      {/* Los `border-*-300/400` de los tres bloques no están en la tabla de
          `index.css`, así que llevan su `dark:` explícito: un tono claro de
          franja se diluye sobre la tarjeta oscura justo cuando más hace falta
          distinguir lo bueno de lo mejorable. */}
      {love && (
        <BloqueResena
          bloque={love}
          rotulo="Lo que más gustó"
          claseRotulo="text-emerald-600"
          claseCaja="border-l-2 border-emerald-300 dark:border-emerald-500 pl-2"
          claseTexto="text-sm leading-relaxed"
          recortar
        />
      )}

      {improve && (
        <BloqueResena
          bloque={improve}
          rotulo="Lo mejorable"
          claseRotulo="text-rose-600"
          claseCaja="border-l-2 border-rose-400 dark:border-rose-500 pl-2"
          claseTexto="text-sm leading-relaxed"
        />
      )}

      {/* `bg-amber-50` sin variante `dark:`: la tabla de `index.css` ya la
          reescribe en oscuro, y añadir aquí una segunda regla sería una tercera
          verdad sobre el mismo color. */}
      {feedback && (
        <BloqueResena
          bloque={feedback}
          rotulo="Nota privada al anfitrión"
          claseRotulo="text-amber-800"
          claseCaja="bg-amber-50 rounded-lg p-2 border-l-2 border-amber-400 dark:border-amber-500"
          claseTexto="text-sm leading-relaxed"
        />
      )}

      {(flojas.length > 0 || sinTraducir) && (
        <div className="flex flex-wrap gap-1.5">
          {/* `bg-rose-100`/`text-rose-800` no están en la tabla de oscuro de
              `index.css`, de ahí el `dark:` a mano. */}
          {flojas.map((s) => (
            <span
              key={s.clave}
              className="badge bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300 text-[10px]"
            >
              {s.etiqueta} {fmt(s.sobre5, 1)}
            </span>
          ))}
          {/* La explicación va escrita, no en un `title`: en un móvil no hay
              ratón y «Sin traducir» a secas se lee como un fallo del CRM. */}
          {sinTraducir && (
            <span className="badge bg-slate-100 text-slate-600 text-[10px]">
              Sin traducir · {idioma} · pendiente del cron
            </span>
          )}
        </div>
      )}

      {/* Solo lectura: responder exigiría escribir en Hostify, que no es lo que
          hace este CRM. */}
      {!!resena.respuesta?.trim() && (
        <div className="bg-slate-50 border border-slate-100 rounded-lg p-2">
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
            Nuestra respuesta
          </span>
          <p className="text-[13px] italic leading-relaxed">{resena.respuesta}</p>
        </div>
      )}

      {/* El plan no se alimenta a mano: se construye solo con las reseñas. Lo que
          se puede hacer desde aquí es ver dónde ha caído ésta. */}
      {resena.enPlan.length > 0 && (
        <Link
          to={`/reviews/plan?listingId=${encodeURIComponent(listingId)}`}
          className="flex items-center min-h-11 text-[11px] text-slate-500 underline underline-offset-2"
        >
          En el plan:{' '}
          {resena.enPlan
            .map((p) =>
              p.estado === 'pendiente'
                ? p.etiqueta
                : `${p.etiqueta} (${ETIQUETA_ESTADO[p.estado].toLowerCase()})`,
            )
            .join(', ')}
        </Link>
      )}
    </article>
  );
}

// ─── Gráfico de escritorio ──────────────────────────────────────────────────

/**
 * Nota media mensual de los últimos 24 meses.
 *
 * Solo se monta a partir de `lg:`. En el móvil no aporta nada que la lista y el
 * delta no digan mejor, y sí aporta el coste de recharts en una pantalla que se
 * recorre con el pulgar.
 */
function GraficoMensual({ serie }: { serie: { mes: string; n: number; nota5: number | null }[] }) {
  const tema = useChartTheme();
  const datos = serie.slice(-24);
  if (datos.filter((d) => d.nota5 !== null).length < 2) return null;

  return (
    <div className="card p-3">
      <p className="text-[12px] text-slate-500 mb-2">Nota media por mes</p>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={datos} margin={{ top: 4, right: 8, bottom: 0, left: -24 }}>
          <CartesianGrid stroke={tema.grid} vertical={false} />
          <XAxis
            dataKey="mes"
            tickFormatter={monthLabel}
            tick={{ fontSize: 10, fill: tema.axis }}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            domain={[3, 5]}
            ticks={[3, 4, 5]}
            tick={{ fontSize: 10, fill: tema.axis }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ stroke: tema.cursor }}
            labelFormatter={(m) => monthLabel(String(m))}
            formatter={(v: any) => [fmt(Number(v), 2), 'Nota media']}
          />
          <Line
            type="monotone"
            dataKey="nota5"
            stroke={tema.bar}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Pantalla ───────────────────────────────────────────────────────────────

export default function ReviewsListingPage() {
  const { listingId = '' } = useParams<{ listingId: string }>();
  const esEscritorio = useAnchoMinimo(1024);
  /**
   * A partir de `sm:` los chips pueden llevar su recuento. En 375 px no: los
   * tres con cifra miden unos 385 px y el ancho útil son 343, así que la fila
   * se saldría de la pantalla. El recuento de lo que se está viendo sigue al
   * pie de la lista («34 de 87»), que es donde hace falta.
   */
  const cabenRecuentos = useAnchoMinimo(640);

  /**
   * La ventana de la sección se recuerda entre pantallas, pero la ficha de un
   * piso arranca SIEMPRE en el histórico completo: aquí se lee la historia del
   * alojamiento, no se compara con la cartera. Si el gestor cambia el rango
   * desde aquí sí se recuerda, porque entonces lo ha pedido a propósito.
   */
  const { setRango: recordarRango } = useRangoResenas();
  const [rango, setRangoVista] = useState<RangoResenas>('todo');
  const cambiarRango = useCallback(
    (r: RangoResenas) => {
      setRangoVista(r);
      recordarRango(r);
    },
    [recordarRango],
  );

  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [canal, setCanal] = useState<CanalResena | ''>('');
  const [busqueda, setBusqueda] = useState('');
  const [texto, setTexto] = useState('');
  const [buscando, setBuscando] = useState(false);

  const [extra, setExtra] = useState<ResenaItem[]>([]);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [errorMas, setErrorMas] = useState<string | null>(null);
  /**
   * `total` se calcula en la petición inicial y la lista se recorre por índice.
   * Si el cron reconstruye la lista entre dos páginas, la cuenta puede quedar
   * por encima de lo que hay y el botón «Cargar 25 más» se volvería un botón
   * que no carga nada. Cuando una página vuelve sin ninguna reseña nueva, se
   * da por terminada.
   */
  const [agotado, setAgotado] = useState(false);

  const parametros = useMemo(() => {
    const p: Record<string, string> = { rango, filtro, limite: String(POR_PAGINA) };
    if (canal) p.canal = canal;
    if (busqueda) p.q = busqueda;
    return p;
  }, [rango, filtro, canal, busqueda]);

  const { data, loading, error } = useApi<DetalleListing>(
    () => api.getDetalleListing(listingId, { ...parametros, offset: '0' }),
    [listingId, rango, filtro, canal, busqueda],
  );

  // Las páginas acumuladas son de la combinación anterior de filtros: al
  // cambiarlos hay que tirarlas, o la lista mezclaría dos consultas distintas.
  useEffect(() => {
    setExtra([]);
    setErrorMas(null);
    setAgotado(false);
  }, [listingId, rango, filtro, canal, busqueda]);

  const resenas = useMemo(() => (data ? [...data.resenas, ...extra] : []), [data, extra]);

  /** Quedan reseñas por traer: ni se agotó la lista ni se han cargado todas. */
  const quedanPorCargar = !agotado && !!data && resenas.length < data.total;

  const cargarMas = useCallback(async () => {
    setCargandoMas(true);
    setErrorMas(null);
    try {
      const mas = await api.getDetalleListing(listingId, {
        ...parametros,
        offset: String(resenas.length),
      });
      // Se descartan las que ya estén: el índice se calcula sobre una lista en
      // memoria que el cron puede haber reconstruido entre dos peticiones, y una
      // reseña repetida con la misma `key` rompería la lista.
      const vistas = new Set(resenas.map((r) => r.id));
      const nuevas = mas.resenas.filter((r) => !vistas.has(r.id));
      if (nuevas.length === 0) setAgotado(true);
      else setExtra((previas) => [...previas, ...nuevas]);
    } catch (e: any) {
      setErrorMas(e?.message ?? 'No se han podido cargar más reseñas');
    } finally {
      setCargandoMas(false);
    }
  }, [listingId, parametros, resenas]);

  // Trae la página siguiente al acercarse el final, sin botón de por medio.
  const centinela = useScrollInfinito(cargarMas, {
    activo: quedanPorCargar,
    cargando: cargandoMas,
  });

  const cerrarBusqueda = useCallback(() => {
    setBuscando(false);
    setTexto('');
    setBusqueda('');
  }, []);

  const ficha = data?.ficha;
  const subnotas = data?.subnotas ?? [];
  const comunes = subnotas.filter((s) => s.comun);
  const soloBooking = subnotas.filter((s) => !s.comun && s.canales.includes('booking'));
  const soloAirbnb = subnotas.filter((s) => !s.comun && !s.canales.includes('booking'));
  const hayPorCanal = soloBooking.length > 0 || soloAirbnb.length > 0;

  const bloquesPorCanal = (
    <div className="space-y-3">
      {soloAirbnb.length > 0 && <GrupoSubnotas titulo="Solo Airbnb" subnotas={soloAirbnb} />}
      {soloBooking.length > 0 && <GrupoSubnotas titulo="Solo Booking" subnotas={soloBooking} />}
    </div>
  );

  return (
    <div className="space-y-3">
      <Link
        to="/reviews"
        className="flex items-center gap-1.5 h-11 -ml-1 text-sm text-slate-600 dark:text-slate-300"
      >
        <ChevronLeft size={18} />
        Reseñas
      </Link>

      {error && <div className="card py-3 text-sm text-amber-700 dark:text-amber-300">{error}</div>}

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-slate-400" size={28} />
        </div>
      ) : !data || !ficha ? (
        !error && <div className="card text-center py-12 text-slate-500">Alojamiento no encontrado.</div>
      ) : (
        <div className="lg:grid lg:grid-cols-[20rem_1fr] lg:gap-8 lg:items-start">
          {/* Columna izquierda en escritorio. Se le pone su propio scroll porque
              con las sub-notas abiertas y el gráfico puede pasar del alto de la
              ventana, y entonces el final quedaría inalcanzable. */}
          <aside className="space-y-3 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:overflow-x-hidden lg:pr-1">
            {/* Cabecera. No se colapsa al hacer scroll: cambiar de 104 px a
                44 px al cruzar los 120 px exige un listener y provoca un reflow
                con el pulgar en marcha, y todo para decorar. */}
            <header className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-lg font-bold leading-tight line-clamp-2">{ficha.nombre}</h1>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {ficha.zona && <span className="text-xs text-slate-500">{ficha.zona}</span>}
                  <BadgesListing badges={ficha.badges} />
                </div>
              </div>
              <div className="flex flex-col items-end shrink-0">
                <NotaGrande
                  nota={ficha.nota5}
                  escala="3xl"
                  atenuada={ficha.badges.includes('pocas')}
                />
                <span className="mt-1">
                  <Estrellas nota={ficha.nota5} size={14} />
                </span>
              </div>
            </header>

            <p className="text-xs text-slate-500">
              {[
                `${ficha.nResenas.toLocaleString('es-ES')} ${ficha.nResenas === 1 ? 'reseña' : 'reseñas'}`,
                rangoDeAnios(ficha.primera, ficha.ultima),
                hace(ficha.ultima) && `última ${hace(ficha.ultima)}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>

            {/* La nativa por canal, siempre visible y en gris. Es la única forma
                de explicar por qué un 8,4 de Booking sale como 4,2 al lado de un
                Airbnb de 4,8, y no se pinta de rojo: esa brecha es sesgo de
                escala y de cultura de nota, no un defecto del piso. */}
            {ficha.porCanal.length > 0 && (
              <p className="text-xs text-slate-500 flex flex-wrap gap-x-2 gap-y-1">
                {ficha.porCanal.map((c, i) => (
                  <span key={c.canal}>
                    <EtiquetaCanal
                      canal={c.canal}
                      n={c.n}
                      nota5={c.nota5}
                      nativa={c.nativa}
                      escala={c.escala}
                    />
                    {i < ficha.porCanal.length - 1 && <span aria-hidden="true"> ·</span>}
                  </span>
                ))}
              </p>
            )}

            {comunes.length > 0 ? (
              <div>
                {comunes.map((s) => (
                  <BarraSubnota key={s.clave} etiqueta={s.etiqueta} sobre5={s.sobre5} n={s.n} />
                ))}
              </div>
            ) : (
              // Airbnb y Booking solo mandan sub-notas cuando el huésped las
              // rellena. Sin esta línea el hueco se lee como un fallo de carga.
              !hayPorCanal && (
                <p className="text-xs text-slate-500">
                  Este alojamiento todavía no tiene sub-notas de limpieza, ubicación ni
                  calidad-precio en el periodo elegido.
                </p>
              )
            )}

            {/* En escritorio hay sitio para los tres bloques abiertos; en el
                móvil, no: seis barras más empujarían la primera reseña fuera de
                pantalla. */}
            {hayPorCanal &&
              (esEscritorio ? (
                bloquesPorCanal
              ) : (
                <details className="card p-0 overflow-hidden">
                  <summary className="flex items-center h-11 px-3 text-sm cursor-pointer select-none">
                    Sub-notas por canal
                  </summary>
                  <div className="px-3 pb-3">{bloquesPorCanal}</div>
                </details>
              ))}

            {/* La misma auditoría que se ve en el plan, aquí también: quien
                abre la ficha de un piso para leer sus reseñas es quien quiere
                saber qué hacer con él, y obligarle a ir al plan para pedirla
                era un viaje de ida y vuelta sin motivo. Comparte el documento
                guardado: generarla en un sitio la deja hecha en el otro. */}
            {ficha.temasAbiertos > 0 && (
              <AuditoriaListing listingId={ficha.listingId} nombre={ficha.nombre} rango={rango} />
            )}

            {ficha.temasAbiertos > 0 && (
              <Link
                to={`/reviews/plan?listingId=${encodeURIComponent(ficha.listingId)}`}
                // `min-h-12` y no `h-12`: con dos temas y un crítico el texto ya
                // ocupa dos líneas en 375 px, y una altura fija lo cortaría.
                className="flex items-center justify-between gap-2 min-h-12 py-2 px-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800"
              >
                <span>
                  {ficha.temasAbiertos === 1
                    ? '1 tema a revisar en este alojamiento'
                    : `${ficha.temasAbiertos.toLocaleString('es-ES')} temas a revisar en este alojamiento`}
                  {ficha.criticos > 0 &&
                    ` · ${ficha.criticos.toLocaleString('es-ES')} crítico${ficha.criticos === 1 ? '' : 's'}`}
                </span>
                <ChevronRight size={18} className="shrink-0" />
              </Link>
            )}

            {/* Periodo y canal van juntos y fuera de la fila pegajosa: los dos
                dicen «de qué reseñas hablamos», y en 375 px no caben con los
                chips en la misma línea. */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Mismo motivo que en la fila de filtros: los botones del
                  selector se quedan en 40 px por sí solos, y estirados dentro
                  de una caja de 48 px pasan de los 44 px de dedo. */}
              <div className="flex items-stretch h-12">
                <SelectorRango rango={rango} onChange={cambiarRango} />
              </div>
              <select
                className="input h-11 w-28 shrink-0"
                aria-label="Filtrar por canal"
                value={canal}
                onChange={(e) => setCanal(e.target.value as CanalResena | '')}
              >
                <option value="">Todos</option>
                {CANALES.map((c) => (
                  <option key={c} value={c}>
                    {ETIQUETA_CANAL[c]}
                  </option>
                ))}
              </select>
              {loading && <Loader2 className="animate-spin text-slate-400" size={16} />}
            </div>

            {esEscritorio && <GraficoMensual serie={data.serie} />}
          </aside>

          {/* Una sola columna a 68ch: esto se lee, no se ojea. */}
          <div className="mt-3 lg:mt-0 max-w-[68ch] space-y-3">
            {/* Los filtros viven aquí y no en la columna izquierda a propósito:
                un `sticky` solo se pega mientras se ve su contenedor, y en el
                móvil la columna izquierda se acaba antes de la primera reseña,
                así que desde la aside los filtros se despegarían justo cuando
                empieza la lista que filtran.
                `top-14` y no `top-0` porque la barra superior del móvil ocupa
                fija esos primeros 56 px; en `lg:` no existe y se alinea con los
                24 px de la columna izquierda.
                `items-stretch` y no `items-center`: los chips ya cumplen sus
                44 px por su cuenta, pero estirados ocupan los 48 px de la fila
                entera y no dejan dos franjas muertas donde el dedo falla. */}
            <div className="sticky top-14 lg:top-6 z-20 -mx-4 px-4 lg:mx-0 lg:px-0 h-12 flex items-stretch gap-2 bg-sereno-white">
              {buscando ? (
                <form
                  className="flex items-center gap-2 w-full"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setBusqueda(texto.trim());
                  }}
                >
                  <div className="relative flex-1 min-w-0">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      size={16}
                    />
                    <input
                      className="input h-11 pl-9"
                      placeholder="Buscar en las reseñas…"
                      value={texto}
                      autoFocus
                      onChange={(e) => setTexto(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={cerrarBusqueda}
                    aria-label="Cerrar la búsqueda"
                    className="w-11 h-11 shrink-0 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5"
                  >
                    <X size={18} />
                  </button>
                </form>
              ) : (
                <>
                  <Chip
                    activo={filtro === 'todas'}
                    onClick={() => setFiltro('todas')}
                    count={cabenRecuentos ? data.contadores.todas : undefined}
                  >
                    Todas
                  </Chip>
                  <Chip
                    activo={filtro === 'negativas'}
                    onClick={() => setFiltro('negativas')}
                    count={cabenRecuentos ? data.contadores.negativas : undefined}
                  >
                    Negativas
                  </Chip>
                  <Chip
                    activo={filtro === 'texto'}
                    onClick={() => setFiltro('texto')}
                    count={cabenRecuentos ? data.contadores.texto : undefined}
                  >
                    Con texto
                  </Chip>
                  {/* La lupa sustituye la fila entera en vez de convivir con
                      ella: un buscador permanente se come 48 px que en 375 px
                      valen más como reseña. */}
                  <button
                    type="button"
                    onClick={() => setBuscando(true)}
                    aria-label="Buscar en las reseñas"
                    className="w-11 h-11 shrink-0 ml-auto self-center grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5"
                  >
                    <Search size={18} />
                  </button>
                </>
              )}
            </div>

            {resenas.length === 0 ? (
              // El vacío dice también por dónde salir: con tres filtros, un
              // canal y una búsqueda encima, «no hay resultados» a secas deja
              // al gestor adivinando cuál de los cinco lo ha vaciado.
              <div className="card text-center py-12 px-4 text-sm text-slate-500">
                {busqueda ? (
                  <>
                    Ninguna reseña contiene «{busqueda}».
                    <br />
                    Prueba con otra palabra o cierra la búsqueda con la X.
                  </>
                ) : data.contadores.todas === 0 ? (
                  <>
                    Ninguna reseña{canal && ` de ${ETIQUETA_CANAL[canal]}`} en este periodo.
                    <br />
                    {canal
                      ? 'Pon el canal en «Todos» o amplía el periodo.'
                      : 'Amplía el periodo a «Todo» para ver el histórico completo.'}
                  </>
                ) : (
                  <>
                    Ninguna reseña con este filtro.
                    <br />
                    Vuelve a «Todas» para verlas todas.
                  </>
                )}
              </div>
            ) : (
              resenas.map((r, i) => {
                const cambiaTemporada = i === 0 || resenas[i - 1].temporada !== r.temporada;
                const temporada = data.temporadas.find((t) => t.clave === r.temporada);
                return (
                  // Un `Fragment` y no un `div` por reseña: `sticky` solo se
                  // pega dentro de su contenedor, y envolver cada tarjeta en su
                  // propio bloque dejaría la cabecera de temporada pegada
                  // durante una sola tarjeta en vez de durante toda la
                  // temporada, que es justo lo que tiene que durar.
                  <Fragment key={r.id}>
                    {/* Da posición y ritmo entre doscientas tarjetas iguales.
                        Se pega justo debajo de los filtros del móvil. */}
                    {cambiaTemporada && (
                      <h2 className="sticky top-[6.5rem] lg:top-[4.5rem] z-10 -mx-4 px-4 lg:mx-0 lg:px-0 h-8 flex items-center bg-sereno-white text-[11px] uppercase tracking-wide text-slate-500">
                        {[
                          temporada?.etiqueta ?? r.temporadaEtiqueta,
                          temporada &&
                            `${temporada.n.toLocaleString('es-ES')} ${temporada.n === 1 ? 'reseña' : 'reseñas'}`,
                          temporada?.nota5 != null && fmt(temporada.nota5, 1),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </h2>
                    )}
                    <TarjetaResena resena={r} listingId={ficha.listingId} />
                  </Fragment>
                );
              })
            )}

            {/* Acumula al bajar, nunca reemplaza: paginar con números rompe la
                lectura y obliga a recordar por dónde ibas. */}
            <PieInfinito
              centinela={centinela}
              cargando={cargandoMas}
              error={errorMas}
              onReintentar={cargarMas}
              mostrarAgotado={resenas.length > 0 && quedanPorCargar === false}
              agotadoTexto={`${resenas.length.toLocaleString('es-ES')} reseñas, no hay más`}
            />

            {resenas.length > 0 && quedanPorCargar && (
              <p className="text-center text-[11px] text-slate-400 tabular-nums">
                {resenas.length.toLocaleString('es-ES')} de {data.total.toLocaleString('es-ES')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sub-notas que solo puntúa un canal.
 *
 * Van rotuladas y aparte de las comunes porque «Comunicación 4,2» de Airbnb y
 * «Personal 8,6» de Booking no son la misma pregunta: mezclarlas en una lista
 * sola invitaría a compararlas, y comparar dos preguntas distintas es peor que
 * no tener el dato.
 */
function GrupoSubnotas({ titulo, subnotas }: { titulo: string; subnotas: SubnotaAgregada[] }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{titulo}</p>
      {subnotas.map((s) => (
        <BarraSubnota key={s.clave} etiqueta={s.etiqueta} sobre5={s.sobre5} n={s.n} />
      ))}
    </div>
  );
}
